import { useMemo, useState } from "react";
import PageWrapper from "../PageWrapper/PageWrapper";
import PrimaryButton from "../../components/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../components/SecondaryButton/SecondaryButton";
import { Modal } from "../../components/Modal/Modal";
import BetaWarning from "../../components/BetaWarning/BetaWarning";
import { geminiService, GeminiImageGenerationRequest } from "../../services/geminiService";
import { toastService } from "../../services/ToastService";
import ex1Before from "../../assets/images/ai-examples/vivienne-back.jpeg";
import ex1After from "../../assets/images/ai-examples/vivienne-after.jpg";
import ex2Before from "../../assets/images/ai-examples/poprostu.jpg";
import ex2After from "../../assets/images/ai-examples/poprostu-after-light.jpg";
import ex3Before from "../../assets/images/ai-examples/sorrenta.webp";
import ex3After from "../../assets/images/ai-examples/sorreta-after.jpg";
import ex4Before from "../../assets/images/ai-examples/aurora.jpg";
import ex4After from "../../assets/images/ai-examples/aurora-after.jpg";
import { useTranslation } from "react-i18next";
import FileUploadWrapper from "../../components/FileUploadWrapper/FileUploadWrapper";
import CONFIG from "../../config/config";
import { datadogRum } from '@datadog/browser-rum';
import "./SocialContentPage.css";
import { ASPECT_RATIO_OPTIONS, getImageEditorPrompts, ImageEditorPrompt } from "./prompts/imageEditorPrompts";

type GenerationKind = "image" | "video";
type Status = "idle" | "uploading" | "processing" | "completed" | "error";

type GeneratedAsset = {
  id: string;
  kind: GenerationKind;
  url: string;
  createdAt: string;
};

type PlaceholderValues = Record<string, string>;

type BeforeAfterExample = {
  id: string;
  captionKey: string;
  beforeUrl: string;
  afterUrl: string;
};

function BeforeAfter({ beforeUrl, afterUrl, captionKey }: BeforeAfterExample) {
  const [position, setPosition] = useState<number>(50);
  const { t } = useTranslation();

  return (
    <div className="w-full">
      <div className="relative w-full rounded-lg overflow-hidden border bg-gray-50" style={{ height: 340 }}>
        {/* Base = After (right side visible), overlayed Before on the left */}
        <img
          src={afterUrl}
          alt="After"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ width: `${position}%` }}
        >
          <img
            src={beforeUrl}
            alt="Before"
            className="h-full w-full object-cover"
          />
        </div>
        <div
          className="absolute top-0 bottom-0" 
          style={{ left: `calc(${position}% - 1px)` }}
        >
          <div className="h-full w-0.5 bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.15)]" />
        </div>
        <input
          type="range"
          min={0}
          max={100}
          value={position}
          onChange={(e) => setPosition(Number(e.target.value))}
          className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
          aria-label={t('social_content.examples.aria_label') as string}
        />
        <div className="absolute left-2 top-2 text-[10px] font-semibold px-2 py-1 rounded bg-black/50 text-white">{t('social_content.examples.before')}</div>
        <div className="absolute right-2 top-2 text-[10px] font-semibold px-2 py-1 rounded bg-black/50 text-white">{t('social_content.examples.after')}</div>
      </div>
      <div className="mt-2 text-xs text-gray-600 text-center">{t(captionKey)}</div>
    </div>
  );
}

const examplePairs: BeforeAfterExample[] = [
  {
    id: "ex-1",
    captionKey: "social_content.examples.captions.ex1",
    beforeUrl: ex2Before,
    afterUrl: ex2After,
  },
  {
    id: "ex-2",
    captionKey: "social_content.examples.captions.ex2",
    beforeUrl: ex1Before,
    afterUrl: ex1After,
  },
  {
    id: "ex-3",
    captionKey: "social_content.examples.captions.ex3",
    beforeUrl: ex3Before,
    afterUrl: ex3After,
  },
  {
    id: "ex-4",
    captionKey: "social_content.examples.captions.ex4",
    beforeUrl: ex4Before,
    afterUrl: ex4After,
  }
];

function useGeminiGeneration() {
  const [status, setStatus] = useState<Status>("idle");
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  const start = async (prompt: string, imageData?: string | string[], aspectRatio?: string) => {
    setStatus("uploading");
    setProgress(10);
    setError(null);

    try {
      // Simulate upload progress
      await new Promise(resolve => setTimeout(resolve, 500));
      setProgress(30);
      setStatus("processing");

      const request: GeminiImageGenerationRequest = {
        prompt,
        imageData,
        model: "gemini-2.5-flash-image",
        aspectRatio
      };

      // Simulate processing progress
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(90, prev + Math.random() * 10));
      }, 200);

      const response = await geminiService.generateImage(request);

      clearInterval(progressInterval);
      setProgress(100);

      if (response.success && response.imageUrl) {
        setStatus("completed");
        return response.imageUrl;
      } else {
        setStatus("error");
        setError(response.error || "Failed to generate image");
        return null;
      }
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Unknown error occurred");
      return null;
    }
  };

  const reset = () => {
    setProgress(0);
    setStatus("idle");
    setError(null);
  };

  return { status, progress, error, start, reset } as const;
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Icon mapping for filter group tabs
const getGroupIcon = (group: string) => {
  const iconClass = "w-3 h-3 inline-block mr-1.5";
  switch (group) {
    case "Model":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      );
    case "Posture & Motion":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      );
    case "Scene":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      );
    case "Lighting & Style":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      );
    case "Composition / Physics":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
        </svg>
      );
    case "Technical":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    default:
      return null;
  }
};

// Icon mapping for filter categories
const getCategoryIcon = (category: string) => {
  const iconClass = "w-3 h-3 inline-block mr-1.5";
  switch (category) {
    case "Hair Color":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
        </svg>
      );
    case "Hair Length & Texture":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      );
    case "Hair Style":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
      );
    case "Makeup":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
      );
    case "Eye Color":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      );
    case "Hair & Makeup":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
      );
    case "Expressions":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case "Poses":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      );
    case "Posture & Motion":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      );
    case "Pose Details":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
      );
    case "Head Tilt":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
        </svg>
      );
    case "Arms & Hands":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
      );
    case "Legs":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      );
    case "Posture":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      );
    case "Body Direction":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      );
    case "Motion":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      );
    case "Body Proportions":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      );
    case "Skin Detail":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      );
    case "Backgrounds":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    case "Classic & Architectural":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      );
    case "Natural & Outdoor":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case "Modern & Urban":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    case "Dreamlike & Seasonal":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      );
    case "Destination & Cultural":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    case "Lighting":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      );
    case "Style":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
        </svg>
      );
    case "Framing":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
        </svg>
      );
    case "Camera Angle":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    case "Targeted Edit":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      );
    case "Cleanup / Retouch":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      );
    case "Art Direction":
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
        </svg>
      );
    default:
      return (
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
        </svg>
      );
  }
};

function SocialContentPage() {
  const { t } = useTranslation();
  const [kind, setKind] = useState<GenerationKind>("image");
  
  // Force kind to always be "image" since video is disabled
  const handleKindChange = (newKind: GenerationKind) => {
    if (newKind === "image") {
      setKind(newKind);
    }
    // Ignore video selection
  };
  const [modelImages, setModelImages] = useState<string[]>([]);
  const [prompt, setPrompt] = useState<string>("");
  const [outputs, setOutputs] = useState<GeneratedAsset[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<ImageEditorPrompt | null>(null);
  const [placeholderValues, setPlaceholderValues] = useState<PlaceholderValues>({});
  const [aspectRatio, setAspectRatio] = useState<string>(ASPECT_RATIO_OPTIONS.find(opt => opt.value === "9:16")?.value || ASPECT_RATIO_OPTIONS[0].value);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [feedbackText, setFeedbackText] = useState<string>("");
  const [showExamples, setShowExamples] = useState<boolean>(true);
  const [activeFilterGroup, setActiveFilterGroup] = useState<string>("Model");
  const [hasGeneratedWithCurrentBatch, setHasGeneratedWithCurrentBatch] = useState<boolean>(false);
  const { status, progress, error, start, reset } = useGeminiGeneration();
  
  const templates = useMemo(() => getImageEditorPrompts(t), [t]);
  const statusLabel = useMemo(() => {
    switch (status) {
      case "idle":
        return t('social_content.status.idle') as string;
      case "uploading":
        return t('social_content.status.uploading') as string;
      case "processing":
        return t('social_content.status.processing') as string;
      case "completed":
        return t('social_content.status.completed') as string;
      case "error":
        return t('social_content.status.error') as string;
      default:
        return status;
    }
  }, [status, t]);

  const isReadyToGenerate = useMemo(() => modelImages.length > 0 && (!!selectedTemplate || prompt.length > 2), [modelImages, selectedTemplate, prompt]);

  const onFileUpload = async (files: File | FileList) => {
    const fileArray = files instanceof FileList ? Array.from(files) : [files];
    if (fileArray.length === 0) return;
    
    const urls = await Promise.all(
      fileArray.map(file => readFileAsDataUrl(file))
    );
    
    // If there are outputs and we haven't generated with current batch yet, 
    // this is a new batch - clear outputs
    if (outputs.length > 0 && !hasGeneratedWithCurrentBatch) {
      setOutputs([]);
      setModelImages(urls.slice(0, 10));
    } else {
      // Add new images to existing ones (max 10 images) - continuing current batch
      setModelImages(prev => [...prev, ...urls].slice(0, 10));
    }
  };

  const removeImage = (index: number) => {
    setModelImages(prev => prev.filter((_, i) => i !== index));
  };

  const generate = async (promptToUse?: string, aspectRatioOverride?: string) => {
    // If template has placeholders, use the final prompt with values replaced
    let finalPrompt = promptToUse || prompt;
    if (selectedTemplate?.placeholders) {
      finalPrompt = getFinalPrompt(selectedTemplate, placeholderValues);
    }
    
    // If aspect ratio is being changed and we have images but no template/prompt,
    // use a default prompt to regenerate with new aspect ratio
    const isAspectRatioChange = aspectRatioOverride !== undefined;
    if (isAspectRatioChange && !selectedTemplate && finalPrompt.length <= 2) {
      finalPrompt = "Output the same image in a different aspect ratio"; // Default prompt for aspect ratio changes
    }
    
    if (modelImages.length === 0 || (!selectedTemplate && finalPrompt.length <= 2 && !isAspectRatioChange)) return;
    
    reset();
    
    // Extract base64 data from data URLs
    const imageDataArray = modelImages.map(imageUrl => {
      return imageUrl.split(',')[1];
    });

    const imageData = imageDataArray.length === 1 ? imageDataArray[0] : imageDataArray;
    const aspectRatioToUse = aspectRatioOverride || aspectRatio;
    
    // Track to DataDog when actually calling geminiService
    datadogRum.addAction('Generate AI Content', {
      hasTemplate: !!selectedTemplate,
      hasPrompt: finalPrompt.length > 0,
      promptLength: finalPrompt.length,
      aspectRatio: aspectRatioToUse,
      imageCount: modelImages.length
    });
    
    const generatedImageUrl = await start(finalPrompt, imageData, aspectRatioToUse);
    
    if (generatedImageUrl) {
      if (outputs.length === 0 && modelImages.length > 0) {
        // Add all original images to outputs
        const originalAssets = modelImages.map((url, index) => ({
          id: `original-${index}-${Math.random().toString(36).slice(2)}`,
          kind, 
          url,
          createdAt: new Date().toISOString() 
        }));
        setOutputs(prev => [...originalAssets, ...prev]);
      }

      const id = Math.random().toString(36).slice(2);
      const asset: GeneratedAsset = { 
        id, 
        kind, 
        url: generatedImageUrl, 
        createdAt: new Date().toISOString() 
      };
      setOutputs(prev => [asset, ...prev].slice(0, 12));
      
      setModelImages([generatedImageUrl]);
      setHasGeneratedWithCurrentBatch(true); // Mark that we've generated with this batch
      
      // Show success toast when generation is completed
      toastService.showToast(t('social_content.toasts.generate_success')!, 'success');
    }
  };

  // Get final prompt with placeholders replaced
  const getFinalPrompt = (template: ImageEditorPrompt, values: PlaceholderValues): string => {
    let finalPrompt = template.prompt;
    
    // Replace all placeholders with their values
    Object.entries(values).forEach(([key, value]) => {
      const placeholder = `{{${key}}}`;
      finalPrompt = finalPrompt.replace(new RegExp(placeholder, 'g'), value);
    });
    
    return finalPrompt;
  };

  const handleTemplateSelect = async (template: ImageEditorPrompt) => {
    setSelectedTemplate(template);
    
    // Initialize placeholder values with first option for each placeholder
    if (template.placeholders) {
      const initialValues: PlaceholderValues = {};
      Object.entries(template.placeholders).forEach(([key, options]) => {
        initialValues[key] = (options as string[])[0] || "";
      });
      setPlaceholderValues(initialValues);
      setPrompt(getFinalPrompt(template, initialValues));
    } else {
      setPlaceholderValues({});
    setPrompt(template.prompt);
    }
    
    // Don't auto-generate if template has placeholders - let user configure first
    if (!template.placeholders && modelImages.length > 0) {
      await generate(template.prompt);
    }
  };

  return (
    <PageWrapper>
      <>
        {/* Beta Warning */}
        <BetaWarning 
          title={t('social_content.beta.title') as string}
          description={t('social_content.beta.description') as string}
          className="mb-6"
        />

        <div className="w-full grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Left Sidebar - Controls */}
          <div className="lg:col-span-3 space-y-4">
            <div className="rounded-xl bg-white p-4 shadow">
              <h2 className="font-bold text-lg">{t('social_content.creator.title') as string}</h2>
              <div className="mt-3 flex rounded-lg bg-gray-100 p-1 w-fit">
                <button className={`px-3 py-1 rounded-md text-sm ${kind === "image" ? "bg-white shadow" : ""}`} onClick={() => handleKindChange("image")}>{t('social_content.creator.kind.image')}</button>
                <button 
                  className="ml-2 px-3 py-1 rounded-md text-sm bg-gray-200 text-gray-400 cursor-not-allowed" 
                  disabled
                  title={t('social_content.creator.kind.video_soon') as string}
                >
                  {t('social_content.creator.kind.video')}
                </button>
              </div>

              <div className="mt-4">
                <label className="text-sm font-semibold">{t('social_content.upload.label') as string}</label>
                <div className="mt-2 flex items-center gap-3">
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="w-full max-w-full">
                    <FileUploadWrapper
                      label="social_content.upload.drop_text"
                      onChange={onFileUpload}
                      allowedTypes={CONFIG.IMAGE_TYPES}
                      allowedMultipleFiles={true}
                    />
                  </div>
                  </div>
                  {modelImages.length > 0 && (
                    <div className="flex-shrink-0 flex flex-wrap gap-2 max-w-[200px]">
                      {modelImages.map((imageUrl, index) => (
                        <div key={index} className="relative group">
                          <img 
                            src={imageUrl} 
                            alt={`${t('social_content.upload.preview_alt')} ${index + 1}`}
                        className="h-14 w-14 object-cover rounded border border-gray-200"
                      />
                          <button
                            onClick={() => removeImage(index)}
                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                            title="Remove image"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Filters Section */}
              <div className="mt-4">
                <label className="text-sm font-semibold mb-3 block">{t('social_content.template.label') || 'Filters'}</label>
                
                {/* Filter Group Tabs */}
                <div className="mb-3">
                  <div className="flex flex-wrap gap-1.5">
                    {["Model", "Scene", "Posture & Motion", "Lighting & Style", "Composition / Physics", "Technical"].map((group) => (
                  <button 
                        key={group}
                        onClick={() => setActiveFilterGroup(group)}
                        className={`px-2.5 py-1 text-xs rounded-md font-medium transition-colors flex items-center ${
                          activeFilterGroup === group
                            ? 'bg-darker-gold-300 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {getGroupIcon(group)}
                        <span>
                          {group === "Model" && t('social_content.template.groups.model')}
                          {group === "Posture & Motion" && (t('social_content.template.groups.posture_motion') || "Posture & Motion")}
                          {group === "Scene" && t('social_content.template.groups.scene')}
                          {group === "Lighting & Style" && t('social_content.template.groups.lighting')}
                          {group === "Composition / Physics" && t('social_content.template.groups.composition')}
                          {group === "Technical" && t('social_content.template.groups.technical')}
                        </span>
                      </button>
                    ))}
                      </div>
                      </div>

                {/* Filter Options */}
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                  {Object.entries(
                    templates
                      .filter((template: ImageEditorPrompt) => template.group === activeFilterGroup)
                      .reduce((acc, template) => {
                        if (!acc[template.category]) {
                          acc[template.category] = [];
                        }
                        acc[template.category].push(template);
                        return acc;
                      }, {} as Record<string, ImageEditorPrompt[]>)
                  ).map(([category, categoryTemplates]) => (
                    <div key={category} className="mb-4 last:mb-0">
                      <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
                        {getCategoryIcon(category)}
                        <span>{category}</span>
                      </h4>
                      <div className="flex flex-wrap gap-1.5">
                        {categoryTemplates.map((template) => (
                          <button
                            key={template.id}
                            onClick={() => handleTemplateSelect(template)}
                            className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
                              selectedTemplate?.id === template.id
                                ? 'bg-darker-gold-300 text-white shadow-sm'
                                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                            }`}
                            title={template.description}
                          >
                            {template.name}
                  </button>
                        ))}
                </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Placeholder Options */}
              {selectedTemplate?.placeholders && Object.keys(selectedTemplate.placeholders).length > 0 && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="text-xs font-semibold text-gray-700 mb-3">Configure Options</h4>
                  <div className="space-y-3">
                    {Object.entries(selectedTemplate.placeholders).map(([key, options]) => {
                      const label = key
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, l => l.toUpperCase());
                      
                      return (
                        <div key={key}>
                          <label className="text-xs font-medium text-gray-700 block mb-1">
                            {label}
                          </label>
                          <select
                            value={placeholderValues[key] || options[0] || ""}
                            onChange={(e) => {
                              const updatedValues = { ...placeholderValues, [key]: e.target.value };
                              setPlaceholderValues(updatedValues);
                              setPrompt(getFinalPrompt(selectedTemplate, updatedValues));
                            }}
                            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm bg-card text-foreground"
                          >
                            {options.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Custom Prompt */}
              <div className="mt-4">
                <p className="text-xs text-gray-500 mt-1 mb-2">{t('social_content.prompt.helper') as string}</p>
                <textarea className="mt-1 w-full rounded-md border p-2 text-sm" rows={3} placeholder={t('social_content.prompt.placeholder') as string} value={prompt} onChange={(e) => setPrompt(e.target.value)} />
              </div>

              {/* Aspect Ratio Selection */}
              <div className="mt-2">
                <label className="text-sm font-semibold">{t('social_content.aspect_ratio.label')}</label>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {ASPECT_RATIO_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
                        aspectRatio === option.value
                          ? 'bg-darker-gold-300 text-white shadow-sm'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                      }`}
                      onClick={() => {
                        const newAspectRatio = option.value;
                        // Only update and generate if aspect ratio is actually changing
                        if (newAspectRatio !== aspectRatio) {
                          setAspectRatio(newAspectRatio);
                          if (modelImages.length > 0) {
                            generate(undefined, newAspectRatio);
                          }
                        }
                      }}
                      title={option.tags && option.tags.length > 0 ? option.tags.join(", ") : undefined}
                    >
                      {option.label}
                      {option.tags && option.tags.length > 0 && (
                        <span className="ml-1 opacity-75">
                          ({option.tags.join(", ")})
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-5 flex items-center gap-3">
                <PrimaryButton 
                  text={t('social_content.actions.generate')!}
                  fn={generate}
                  isDisabled={!isReadyToGenerate || status === "processing" || status === "uploading"}
                  eventName="social_content_generate"
                  actionName="Generate AI Content"
                />
                <SecondaryButton 
                  text={t('social_content.actions.reset')!}
                  fn={() => { 
                    setModelImages([]); 
                    setSelectedTemplate(null); 
                    setPlaceholderValues({});
                    setPrompt(""); 
                    setOutputs([]);
                    setHasGeneratedWithCurrentBatch(false);
                    reset(); 
                  }}
                  eventName="social_content_reset"
                  actionName="Reset Social Content"
                />
              </div>

              <div className="mt-4 text-sm">
                <div className="flex items-center gap-2">
                  <span 
                    className={`h-2 w-2 rounded-full ${status === "completed" ? "bg-green-500" : status === "processing" ? "bg-blue-500" : status === "uploading" ? "bg-amber-500" : status === "error" ? "bg-red-500" : "bg-gray-400"}`}
                    style={status === "processing" || status === "uploading" ? {
                      animation: 'slowPulse 3s ease-in-out infinite'
                    } : {}}
                  ></span>
                  <span>{statusLabel}</span>
                  {status !== "idle" && status !== "completed" && status !== "error" && <span className="ml-auto text-gray-500">{Math.round(progress)}%</span>}
                </div>
                {status !== "idle" && status !== "error" && (
                  <div className="mt-2 h-2 w-full bg-gray-200 rounded">
                    <div className="h-2 bg-darker-gold-300 rounded" style={{ width: `${progress}%` }} />
                  </div>
                )}
                {status === "error" && error && (
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
                    {error}
                  </div>
                )}
              </div>

              <div className="mt-3">
                <SecondaryButton
                  text={t('social_content.actions.report_issue')!}
                  fn={() => {
                    setShowErrorModal(true);
                  }}
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  }
                  iconPosition="left"
                  actionName="Report Issue"
                />
              </div>
            </div>
          </div>

          {/* Center - Preview and Examples */}
          <div className="lg:col-span-6 space-y-4">
            <div className="rounded-xl bg-white p-4 shadow min-h-[420px] flex items-center justify-center relative overflow-hidden">
              {modelImages.length > 0 ? (
                <div className={`w-full max-w-xl relative ${(status === "processing" || status === "uploading") ? 'magical-loading' : ''}`}>
                  {modelImages.length === 1 ? (
                    <>
                      <img 
                        src={modelImages[0]} 
                        alt="preview" 
                        className={`w-full rounded-xl object-contain transition-all duration-300 ${
                          (status === "processing" || status === "uploading") 
                            ? 'brightness-110 saturate-110' 
                            : ''
                        }`}
                        style={status === "processing" || status === "uploading" ? {
                          animation: 'slowPulse 6s ease-in-out infinite'
                        } : {}}
                      />
                      {(status === "processing" || status === "uploading") && (
                        <>
                          {/* Shimmer overlay */}
                          <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                          {/* Sparkle particles */}
                          <div className="absolute inset-0 rounded-xl pointer-events-none">
                            <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-darker-gold-200 rounded-full animate-sparkle-1"></div>
                            <div className="absolute top-1/3 right-1/3 w-1.5 h-1.5 bg-darker-gold-100 rounded-full animate-sparkle-2"></div>
                            <div className="absolute bottom-1/4 left-1/3 w-2 h-2 bg-darker-gold-200 rounded-full animate-sparkle-3"></div>
                            <div className="absolute top-1/2 right-1/4 w-1.5 h-1.5 bg-darker-gold-100 rounded-full animate-sparkle-4"></div>
                            <div className="absolute bottom-1/3 left-1/2 w-2 h-2 bg-darker-gold-50 rounded-full animate-sparkle-5"></div>
                          </div>
                          {/* Glow effect */}
                          <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-darker-gold-200/20 via-darker-gold-100/20 to-darker-gold-50/20 animate-glow pointer-events-none"></div>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {modelImages.map((imageUrl, index) => (
                        <div 
                          key={index} 
                          className={`relative ${(status === "processing" || status === "uploading") ? 'magical-loading' : ''}`}
                        >
                          <img 
                            src={imageUrl} 
                            alt={`Preview ${index + 1}`}
                            className={`w-full rounded-lg object-cover aspect-square transition-all duration-300 ${
                              (status === "processing" || status === "uploading") 
                                ? 'brightness-110 saturate-110' 
                                : ''
                            }`}
                            style={status === "processing" || status === "uploading" ? {
                              animation: 'slowPulse 6s ease-in-out infinite'
                            } : {}}
                          />
                          {(status === "processing" || status === "uploading") && (
                            <>
                              {/* Shimmer overlay */}
                              <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                              {/* Sparkle particles */}
                              <div className="absolute inset-0 rounded-lg pointer-events-none">
                                <div className="absolute top-1/4 left-1/4 w-1.5 h-1.5 bg-darker-gold-200 rounded-full animate-sparkle-1"></div>
                                <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-darker-gold-100 rounded-full animate-sparkle-2"></div>
                                <div className="absolute bottom-1/4 left-1/3 w-1.5 h-1.5 bg-darker-gold-200 rounded-full animate-sparkle-3"></div>
                                <div className="absolute top-1/2 right-1/4 w-1 h-1 bg-darker-gold-100 rounded-full animate-sparkle-4"></div>
                                <div className="absolute bottom-1/3 left-1/2 w-1.5 h-1.5 bg-darker-gold-50 rounded-full animate-sparkle-5"></div>
                              </div>
                              {/* Glow effect */}
                              <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-darker-gold-200/20 via-darker-gold-100/20 to-darker-gold-50/20 animate-glow pointer-events-none"></div>
                            </>
                          )}
                          <button
                            onClick={() => removeImage(index)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 transition-colors z-10"
                            title="Remove image"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-gray-500 text-sm">{t('social_content.preview.empty') as string}</div>
              )}
            </div>

            <div className="rounded-xl bg-white p-4 shadow">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">{t('social_content.examples.title')}</h3>
                <button
                  onClick={() => setShowExamples((v) => !v)}
                  className="px-3 py-1.5 text-xs rounded-md border hover:bg-gray-50"
                >
                  {showExamples ? t('social_content.examples.toggle_hide') : t('social_content.examples.toggle_show')}
                </button>
              </div>
              {showExamples && (
                <div className="max-h-96 overflow-y-auto pr-1">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {examplePairs.map((ex) => (
                      <BeforeAfter key={ex.id} {...ex} />
                    ))}
                  </div>
                </div>
              )}
            </div>
            </div>

          {/* Right Sidebar - Results/History */}
          <div className="lg:col-span-3">
            <div className="rounded-xl bg-white p-4 shadow sticky top-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-sm">{t('social_content.recent_outputs.title') as string}</h3>
                {outputs.length > 0 && (
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{outputs.length}</span>
                )}
              </div>
              <div className="space-y-3 max-h-[calc(100vh-200px)] overflow-y-auto">
                {outputs.length > 0 ? (
                  outputs.map(o => (
                    <div 
                      key={o.id} 
                      className="rounded-lg overflow-hidden bg-gray-50 border border-gray-200 hover:border-darker-gold-300 transition-colors cursor-pointer group"
                        onClick={() => {
                          console.log('Output image selected as main:', o.url);
                          setModelImages([o.url]);
                          setHasGeneratedWithCurrentBatch(false); // Starting a new batch
                          toastService.showToast("Image selected as base for next generation!", 'success');
                        }}
                    >
                      <div className="relative aspect-square bg-gray-100">
                        <img 
                          src={o.url} 
                          alt={o.id} 
                          className="w-full h-full object-cover" 
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center pointer-events-none">
                      <button
                        onClick={(e) => {
                              e.stopPropagation();
                          const link = document.createElement('a');
                          link.href = o.url;
                          link.download = `generated-image-${o.id}-${Date.now()}.jpg`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          toastService.showToast(t('social_content.toasts.download_success')!, 'success');
                        }}
                            className="opacity-0 group-hover:opacity-100 bg-white/90 hover:bg-white p-2 rounded-md transition-opacity shadow-lg pointer-events-auto"
                        title={t('social_content.recent_outputs.download_title') as string}
                      >
                            <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </button>
                    </div>
                        {o.id.startsWith('original') && (
                          <div className="absolute top-2 left-2 px-2 py-0.5 bg-darker-gold-300 text-white text-[10px] font-semibold rounded pointer-events-none">
                            {t('social_content.recent_outputs.original')}
                          </div>
                        )}
                      </div>
                      <div className="px-2 py-1.5 text-[10px] text-gray-600 flex items-center justify-between">
          <span className={`uppercase ${o.id.startsWith('original') ? 'text-darker-gold-300 font-semibold' : ''}`}>
            {o.id.startsWith('original') ? t('social_content.recent_outputs.original') : o.kind}
          </span>
          <span>{new Date(o.createdAt).toLocaleTimeString()}</span>
        </div>
                  </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <div className="text-sm">{t('social_content.recent_outputs.empty')}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>


        {/* Error Confirmation Modal */}
        {showErrorModal && (
          <Modal
            modalTitle={t('social_content.report.title')}
            canClose={true}
            onClose={() => {
              setFeedbackText("");
              setShowErrorModal(false);
            }}
          >
            <div className="space-y-6">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                  <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
              </div>

              <div className="text-sm text-gray-600">
              {t('social_content.report.description')}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('social_content.report.feedback_label') || 'Feedback'}
                </label>
                <textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder={t('social_content.report.feedback_placeholder') || 'Please describe the issue or provide feedback...'}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  rows={4}
                />
              </div>
              
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => {
                    const errorContext = {
                      timestamp: new Date().toISOString(),
                      userAction: 'issue_report_confirmed',
                      page: 'SocialContentPage',
                      status: status,
                      hasImage: modelImages.length > 0,
                      hasTemplate: !!selectedTemplate,
                      hasPrompt: !!prompt,
                      promptLength: prompt.length,
                      aspectRatio: aspectRatio,
                      feedbackText: feedbackText,
                      sessionData: {
                        outputsCount: outputs.length,
                        lastOutput: outputs[0]?.id || null
                      }
                    };
                    
                    datadogRum.addError(
                      new Error('Social Content Issue Report Confirmed'),
                      {
                        source: 'custom',
                        ...errorContext
                      }
                    );
                    
                    toastService.showToast(t('social_content.toasts.report_received')!, 'success');
                    setFeedbackText("");
                    setShowErrorModal(false);
                  }}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-md transition-colors"
                >
                  {t('social_content.report.submit')}
                </button>
              </div>
            </div>
          </Modal>
        )}

      </>
    </PageWrapper>
  );
}

export default SocialContentPage;


